import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, SignOut, Storefront, Ticket, Copy, Plus, Trash, Key, PencilSimple } from "@phosphor-icons/react";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";

const STATUS_LABELS = {
  active: "Assinatura ativa",
  trialing: "Em teste (Stripe)",
  past_due: "Pagamento pendente",
  canceled: "Cancelada",
  none: "Sem assinatura",
};
const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-400",
  trialing: "bg-amber-500/15 text-amber-500",
  past_due: "bg-orange-500/15 text-orange-400",
  canceled: "bg-red-500/15 text-red-400",
  none: "bg-zinc-700/40 text-zinc-400",
};

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [barbers, setBarbers] = useState(null);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("geral");
  const [codeDays, setCodeDays] = useState("30");
  const [codeCount, setCodeCount] = useState("1");
  const [showCreate, setShowCreate] = useState(false);
  const [newBarber, setNewBarber] = useState({ shop_name: "", whatsapp: "", password: "", country: "br" });
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todas");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [partnerResetOpen, setPartnerResetOpen] = useState(false);
  const [partnerPassword, setPartnerPassword] = useState("");
  const [resettingPartner, setResettingPartner] = useState(false);

  const load = useCallback(async () => {
    const headers = authHeaders();
    try {
      const res = await axios.get(`${API}/superadmin/barbers`, { headers });
      setBarbers(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        return navigate("/entrar");
      }
      if (e.response?.status === 403) {
        try {
          const me = await axios.get(`${API}/auth/me`, { headers });
          return navigate(me.data.role === "partner" ? "/socio" : "/painel");
        } catch {
          clearToken();
          return navigate("/entrar");
        }
      }
      toast.error("Erro ao carregar barbearias");
      setBarbers([]);
    }
    try {
      const res = await axios.get(`${API}/superadmin/codes`, { headers });
      setCodes(res.data);
    } catch {
      // seção de códigos fica vazia, sem derrubar a página
    }
    try {
      const res = await axios.get(`${API}/superadmin/stats`, { headers });
      setStats(res.data);
    } catch {
      // stats opcionais
    }
    try {
      const res = await axios.get(`${API}/superadmin/proofs`, { headers });
      setProofs(res.data);
    } catch {
      // comprovantes opcionais
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) return navigate("/entrar");
    load();
  }, [load, navigate]);

  const logout = () => {
    clearToken();
    navigate("/");
  };

  const pendingProofs = proofs.filter((p) => p.status === "pendente").length;

  const digits = search.replace(/\D/g, "");
  const q = search.toLowerCase().trim();
  const matchesSearch = (b) => {
    if (!q) return true;
    const text = `${b.shop_name} ${b.name} ${b.city} ${b.slug} ${b.email}`.toLowerCase();
    if (text.includes(q)) return true;
    return digits.length >= 3 && (b.whatsapp || "").includes(digits);
  };
  const matchesFilter = (b) => {
    if (filter === "ativas") return b.subscription_active;
    if (filter === "teste") return b.subscription_status === "none" && b.trial_days_left > 0 && b.paid_days_left === 0;
    if (filter === "pausadas") return !b.subscription_active;
    return true;
  };
  const filteredBarbers = (barbers || []).filter((b) => matchesSearch(b) && matchesFilter(b));

  const generateCode = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/codes`,
        { count: Number(codeCount) || 1, days: Number(codeDays) || 30 },
        { headers: authHeaders() }
      );
      const list = res.data.map((c) => c.code);
      if (list.length === 1) {
        try {
          await navigator.clipboard.writeText(list[0]);
          toast.success(`Código gerado e copiado: ${list[0]}`);
        } catch {
          toast.success(`Código gerado: ${list[0]}`);
        }
      } else {
        toast.success(`${list.length} códigos gerados`);
      }
      load();
    } catch {
      toast.error("Erro ao gerar código");
    } finally {
      setGenerating(false);
    }
  };

  const createBarber = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(`${API}/superadmin/barbers`, newBarber, { headers: authHeaders() });
      toast.success(`Conta criada: /b/${res.data.slug}`);
      setNewBarber({ shop_name: "", whatsapp: "", password: "", country: "br" });
      setShowCreate(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar conta");
    } finally {
      setCreating(false);
    }
  };

  const deleteBarber = async (id, name) => {if (!window.confirm(`Excluir "${name}" e todos os dados dela (serviços, agenda, agendamentos)? Essa ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/superadmin/barbers/${id}`, { headers: authHeaders() });
      toast.success("Barbearia excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    if (resetPassword.length < 6) {
      toast.error("A senha precisa de pelo menos 6 caracteres");
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/superadmin/barbers/${resetTarget.id}/reset-password`, { password: resetPassword }, { headers: authHeaders() });
      toast.success(`Senha de "${resetTarget.shop_name}" redefinida. Repasse a nova senha ao barbeiro.`);
      setResetTarget(null);
      setResetPassword("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao redefinir senha");
    } finally {
      setResetting(false);
    }
  };

  const submitPartnerReset = async () => {
    if (partnerPassword.length < 6) {
      toast.error("A senha precisa de pelo menos 6 caracteres");
      return;
    }
    setResettingPartner(true);
    try {
      await axios.post(`${API}/superadmin/partner/reset-password`, { password: partnerPassword }, { headers: authHeaders() });
      toast.success("Senha do sócio redefinida. Repasse a nova senha a ele.");
      setPartnerResetOpen(false);
      setPartnerPassword("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao redefinir senha do sócio");
    } finally {
      setResettingPartner(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Copiado!");
    } catch {
      toast.info(code);
    }
  };

  const deleteCode = async (id) => {
    try {
      await axios.delete(`${API}/superadmin/codes/${id}`, { headers: authHeaders() });
      toast.success("Código excluído");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao excluir código");
    }
  };

  const reviewProof = async (id, action) => {
    try {
      await axios.post(`${API}/superadmin/proofs/${id}/review`, { action }, { headers: authHeaders() });
      toast.success(action === "aprovar" ? "Pagamento aprovado — 30 dias liberados" : "Comprovante recusado");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao analisar");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 py-6 pb-16" data-testid="superadmin-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-500">
            <Scissors size={18} weight="duotone" />
            <span className="font-display font-bold">Barber Menu · Admin</span>
          </div>
          <button
            data-testid="partner-reset-toggle-btn"
            onClick={() => { setPartnerResetOpen(!partnerResetOpen); setPartnerPassword(""); }}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-amber-500 transition-colors"
            title="Redefinir senha do sócio"
          >
            <Key size={16} weight="bold" /> Sócio
          </button>
          <button
            data-testid="superadmin-logout-btn"
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400 transition-colors"
          >
            <SignOut size={16} weight="bold" /> Sair
          </button>
        </div>

        {partnerResetOpen && (
          <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-reset-panel">
            <p className="text-xs text-zinc-400 mb-2">Defina uma senha nova para o sócio (socio@barbergold.app) e repasse a ele:</p>
            <div className="flex gap-2">
              <input
                data-testid="partner-reset-input"
                type="text"
                value={partnerPassword}
                onChange={(e) => setPartnerPassword(e.target.value)}
                placeholder="Nova senha (mín. 6 caracteres)"
                className="flex-1 h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm"
              />
              <button
                data-testid="partner-reset-confirm-btn"
                onClick={submitPartnerReset}
                disabled={resettingPartner || partnerPassword.length < 6}
                className="h-10 px-4 rounded-lg bg-amber-500 text-zinc-950 text-sm font-bold disabled:opacity-40 hover:bg-amber-400 transition-colors"
              >
                {resettingPartner ? "Salvando..." : "Redefinir"}
              </button>
            </div>
          </div>
        )}

        <nav className="mt-6 flex gap-2" data-testid="admin-tabs">
          {[["geral", "Visão geral"], ["codigos", "Códigos de resgate"], ["comprovantes", "Comprovantes"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${
                tab === key ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {label}
              {key === "comprovantes" && pendingProofs > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid="pending-proofs-badge">
                  {pendingProofs}
                </span>
              )}
            </button>
          ))}
        </nav>

        {tab === "geral" && (
          <>
        <div className="grid grid-cols-2 gap-3 mt-6" data-testid="stats-grid">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="stat-total">
            <p className="text-2xl font-black text-zinc-50">{stats?.total ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Barbearias cadastradas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="stat-active">
            <p className="text-2xl font-black text-emerald-400">{stats?.active ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Com página ativa</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="stat-paused">
            <p className="text-2xl font-black text-red-400">{stats?.paused ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Pausadas (vencidas)</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="stat-mrr">
            <p className="text-2xl font-black text-amber-500">
              {stats ? `R$ ${stats.mrr_estimate_brl.toFixed(2).replace(".", ",")}` : "—"}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Receita mensal estimada</p>
          </div>
        </div>

        {stats && (
          <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4 grid grid-cols-2 gap-y-2 text-xs text-zinc-400" data-testid="stats-detail">
            <p>Em teste grátis: <span className="font-bold text-zinc-200">{stats.in_trial}</span></p>
            <p>Ativas por código: <span className="font-bold text-zinc-200">{stats.via_code}</span></p>
            <p>Assinatura Stripe: <span className="font-bold text-zinc-200">{stats.stripe_active}</span></p>
            <p>Agendamentos totais: <span className="font-bold text-zinc-200">{stats.total_bookings}</span></p>
            <p>Brasil: <span className="font-bold text-zinc-200">{stats.br_count}</span> · Paraguai: <span className="font-bold text-zinc-200">{stats.py_count}</span></p>
            <p>Códigos disponíveis: <span className="font-bold text-zinc-200">{stats.codes_available}</span> · usados: <span className="font-bold text-zinc-200">{stats.codes_used}</span></p>
          </div>
        )}
          </>
        )}

        {tab === "geral" && (
          <div className="mt-6">
            <button
              data-testid="toggle-create-barber-btn"
              onClick={() => setShowCreate((v) => !v)}
              className="w-full h-11 rounded-xl border border-dashed border-zinc-700 text-sm font-semibold text-zinc-400 hover:border-amber-500/60 hover:text-amber-500 transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={16} weight="bold" /> Nova barbearia
            </button>
            {showCreate && (
              <form onSubmit={createBarber} className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3 animate-fade-up" data-testid="create-barber-form">
                <input
                  data-testid="new-shop-input"
                  type="text"
                  value={newBarber.shop_name}
                  onChange={(e) => setNewBarber((f) => ({ ...f, shop_name: e.target.value }))}
                  placeholder="Nome da barbearia"
                  className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none"
                />
                <div className="flex gap-2">
                  <input
                    data-testid="new-whatsapp-input"
                    type="tel"
                    value={newBarber.whatsapp}
                    onChange={(e) => setNewBarber((f) => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="WhatsApp (login)"
                    className="flex-1 h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none"
                  />
                  <select
                    data-testid="new-country-select"
                    value={newBarber.country}
                    onChange={(e) => setNewBarber((f) => ({ ...f, country: e.target.value }))}
                    className="h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 focus:border-amber-500 focus:outline-none"
                  >
                    <option value="br">Brasil</option>
                    <option value="py">Paraguai</option>
                  </select>
                </div>
                <input
                  data-testid="new-password-input"
                  type="text"
                  value={newBarber.password}
                  onChange={(e) => setNewBarber((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Senha provisória (mín 6)"
                  className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none"
                />
                <button
                  data-testid="create-barber-submit-btn"
                  type="submit"
                  disabled={creating}
                  className="w-full h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
                >
                  {creating ? "Criando..." : "Criar conta"}
                </button>
              </form>
            )}
          </div>
        )}

        {tab === "codigos" && (
        <section className="mt-6" data-testid="codes-section">
          <h2 className="text-sm font-bold text-zinc-300 flex items-center gap-2 mb-3">
            <Ticket size={16} className="text-amber-500" weight="duotone" /> Códigos de resgate
          </h2>
          <div className="flex gap-2 mb-2" data-testid="code-gen-form">
            <input
              data-testid="code-days-input"
              type="number"
              min="1"
              max="365"
              value={codeDays}
              onChange={(e) => setCodeDays(e.target.value)}
              placeholder="Dias"
              className="w-20 h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none"
            />
            <input
              data-testid="code-count-input"
              type="number"
              min="1"
              max="50"
              value={codeCount}
              onChange={(e) => setCodeCount(e.target.value)}
              placeholder="Qtd"
              className="w-20 h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none"
            />
            <button
              data-testid="generate-code-btn"
              onClick={generateCode}
              disabled={generating}
              className="flex-1 h-10 px-4 rounded-lg bg-amber-500 text-zinc-950 text-xs font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
            >
              {generating ? "Gerando..." : "Gerar código"}
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-3">Escolha os dias de acesso (7, 15, 30, o que quiser) e a quantidade de códigos.</p>
          {codes.length === 0 ? (
            <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-codes-msg">
              Nenhum código gerado. Gere um e envie ao barbeiro após o pagamento.
            </p>
          ) : (
            <div className="space-y-2" data-testid="codes-list">
              {codes.map((c) => (
                <button
                  key={c.id}
                  data-testid={`code-row-${c.id}`}
                  onClick={() => copyCode(c.code)}
                  className="w-full flex items-center justify-between gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-left hover:border-amber-500/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-bold text-zinc-100">{c.code}</p>
                    <p className="text-xs text-zinc-500">
                      {c.days} dias
                      {c.used_by_name ? ` · usado por ${c.used_by_name}` : ""}
                    </p>
                  </div>
                  {c.used_by ? (
                    <span className="text-xs font-bold px-2 py-1 rounded-full bg-zinc-700/40 text-zinc-400 shrink-0" data-testid={`code-status-${c.id}`}>Usado</span>
                  ) : (
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400" data-testid={`code-status-${c.id}`}>
                        <Copy size={12} weight="bold" /> Disponível
                      </span>
                      <button
                        data-testid={`code-delete-btn-${c.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteCode(c.id); }}
                        className="text-zinc-600 hover:text-red-400 transition-colors"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
        )}

        {tab === "comprovantes" && (
          <section className="mt-6" data-testid="proofs-section">
            <h2 className="text-sm font-bold text-zinc-300 mb-3">Comprovantes de pagamento (Paraguai)</h2>
            {proofs.length === 0 ? (
              <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-proofs-msg">
                Nenhum comprovante enviado ainda.
              </p>
            ) : (
              <div className="space-y-3" data-testid="proofs-list">
                {proofs.map((p) => (
                  <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`proof-row-${p.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-bold text-zinc-100 truncate">{p.shop_name}</p>
                        <p className="text-xs text-zinc-500">
                          {p.whatsapp} · {new Date(p.created_at).toLocaleString("pt-BR")}
                        </p>
                      </div>
                      <span
                        data-testid={`proof-status-${p.id}`}
                        className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ${
                          p.status === "aprovado"
                            ? "bg-emerald-500/15 text-emerald-400"
                            : p.status === "recusado"
                              ? "bg-red-500/15 text-red-400"
                              : "bg-amber-500/15 text-amber-500"
                        }`}
                      >
                        {p.status === "aprovado" ? "Aprovado" : p.status === "recusado" ? "Recusado" : "Pendente"}
                      </span>
                    </div>
                    <a
                      href={p.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`proof-file-${p.id}`}
                      className="mt-3 block"
                    >
                      {p.content_type === "application/pdf" ? (
                        <span className="flex items-center justify-center h-20 rounded-lg bg-zinc-950 border border-zinc-800 text-sm text-amber-500 font-semibold">
                          Ver PDF do comprovante
                        </span>
                      ) : (
                        <img
                          src={p.file_url}
                          alt="Comprovante"
                          className="w-full max-h-56 object-contain rounded-lg bg-zinc-950 border border-zinc-800"
                          data-testid={`proof-image-${p.id}`}
                        />
                      )}
                    </a>
                    {p.status === "pendente" && (
                      <div className="flex gap-2 mt-3">
                        <button
                          data-testid={`proof-approve-btn-${p.id}`}
                          onClick={() => reviewProof(p.id, "aprovar")}
                          className="flex-1 h-10 rounded-lg bg-emerald-500 text-zinc-950 text-sm font-bold hover:bg-emerald-400 transition-colors active:scale-95"
                        >
                          Conferido (+30 dias)
                        </button>
                        <button
                          data-testid={`proof-reject-btn-${p.id}`}
                          onClick={() => reviewProof(p.id, "recusar")}
                          className="flex-1 h-10 rounded-lg border border-red-500/40 text-red-400 text-sm font-semibold hover:bg-red-500/10 transition-colors"
                        >
                          Recusar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "geral" && (!barbers ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        ) : barbers.length === 0 ? (
          <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4 mt-6" data-testid="no-barbers-msg">
            Nenhuma barbearia cadastrada ainda.
          </p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="barbers-list">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-zinc-300">Barbearias</h2>
              <span className="text-xs text-zinc-500">{filteredBarbers.length} de {barbers.length}</span>
            </div>
            <input
              data-testid="admin-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, cidade ou telefone..."
              className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
            />
            <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-filters">
              {[["todas", "Todas"], ["ativas", "Ativas"], ["teste", "Em teste"], ["pausadas", "Pausadas"]].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`admin-filter-${key}`}
                  onClick={() => setFilter(key)}
                  className={`shrink-0 px-3.5 h-8 rounded-lg text-xs font-semibold transition-colors ${
                    filter === key ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {filteredBarbers.length === 0 && (
              <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="no-results-msg">
                Nenhuma barbearia encontrada com esse filtro.
              </p>
            )}
            {filteredBarbers.map((b) => (
              <div key={b.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`barber-row-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Storefront size={18} weight="duotone" className="text-amber-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-bold text-zinc-100 truncate flex items-center gap-1.5">
                        <span
                          data-testid={`barber-activation-dot-${b.id}`}
                          title={b.activated ? "Já acessou e definiu a senha" : "Ainda não trocou a senha temporária"}
                          className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${b.activated ? "bg-emerald-500" : "bg-red-500 animate-pulse"}`}
                        />
                        {b.shop_name}
                      </p>
                      <p className="text-xs text-zinc-500 truncate">{b.name} · {b.email || b.whatsapp || "—"}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ${STATUS_STYLES[b.subscription_status] || STATUS_STYLES.none}`} data-testid={`barber-sub-status-${b.id}`}>
                    {STATUS_LABELS[b.subscription_status] || b.subscription_status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-zinc-400">
                  <span className="font-semibold text-zinc-300">{b.country === "py" ? "PY" : "BR"}</span>
                  <span>{b.city || "—"}</span>
                  {b.subscription_status === "none" && b.trial_days_left > 0 && b.paid_days_left === 0 && (
                    <span className="text-amber-500 font-semibold">Teste grátis: {b.trial_days_left}d restantes</span>
                  )}
                  {b.subscription_status === "none" && b.paid_days_left > 0 && (
                    <span className="text-emerald-400 font-semibold">Código: {b.paid_days_left}d restantes</span>
                  )}
                  <span>{b.bookings_count} {b.bookings_count === 1 ? "agendamento" : "agendamentos"}</span>
                  <a
                    href={`/b/${b.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-500 hover:underline"
                    data-testid={`barber-public-link-${b.id}`}
                  >
                    /b/{b.slug}
                  </a>
                  <button
                    data-testid={`barber-edit-btn-${b.id}`}
                    onClick={() => navigate(`/admin/barbearia/${b.id}`)}
                    className="flex items-center gap-1 text-zinc-300 hover:text-amber-500 font-semibold transition-colors"
                  >
                    <PencilSimple size={13} weight="bold" /> Editar
                  </button>
                  <button
                    data-testid={`barber-reset-password-btn-${b.id}`}
                    onClick={() => { setResetTarget(resetTarget?.id === b.id ? null : b); setResetPassword(""); }}
                    className="flex items-center gap-1 text-amber-500/80 hover:text-amber-500 font-semibold transition-colors"
                  >
                    <Key size={13} weight="bold" /> Senha
                  </button>
                  <button
                    data-testid={`barber-delete-btn-${b.id}`}
                    onClick={() => deleteBarber(b.id, b.shop_name)}
                    className="ml-auto flex items-center gap-1 text-red-400/80 hover:text-red-400 font-semibold transition-colors"
                  >
                    <Trash size={13} weight="bold" /> Excluir
                  </button>
                </div>
                {resetTarget?.id === b.id && (
                  <div className="mt-3 pt-3 border-t border-zinc-800" data-testid={`reset-password-panel-${b.id}`}>
                    <p className="text-xs text-zinc-400 mb-2">
                      Por segurança não é possível ver a senha atual (ela é criptografada). Defina uma senha nova e repasse ao barbeiro:
                    </p>
                    <div className="flex gap-2">
                      <input
                        data-testid={`reset-password-input-${b.id}`}
                        type="text"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Nova senha (mín. 6 caracteres)"
                        className="flex-1 h-10 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm"
                      />
                      <button
                        data-testid={`reset-password-confirm-btn-${b.id}`}
                        onClick={submitResetPassword}
                        disabled={resetting || resetPassword.length < 6}
                        className="h-10 px-4 rounded-lg bg-amber-500 text-zinc-950 text-sm font-bold disabled:opacity-40 hover:bg-amber-400 transition-colors"
                      >
                        {resetting ? "Salvando..." : "Redefinir"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
